import { computed, ref, watch } from 'vue';
import type { DataTableColumn } from '@DataTableModule';

const ACTIVE_ROWS_PER_PAGE = 10;

const activeColumns = ref<string[]>([]);
const initialColumns = ref<string[]>([]);

const columns = ref<DataTableColumn[]>([]);
const frozenFields = ref<string[]>([]);
const page = ref(1);
const rows = ref<any[]>([]);
const rowsPerPage = ref(ACTIVE_ROWS_PER_PAGE);
const searchQuery = ref<string>('');
const selectedRows = ref<any[]>([]);
const selectedAnimalIds = ref<Set<string>>(new Set());
const selectedAnimalsById = ref<Map<string, any>>(new Map());
const size = ref<'compact' | 'default' | 'large'>('default');
const totalRecords = ref<number>(0);

const mainRequestUrlParams = computed(() => {
  let params = '';

  if (searchQuery.value) params += `&search=${encodeURIComponent(searchQuery.value)}`;
  if (page.value) params += `&page=${page.value}`;

  return params;
});

watch(rows, syncSelectedRowsFromIds, { deep: true });

function syncSelectedRowsFromIds() {
  if (rows.value.length === 0 || selectedAnimalIds.value.size === 0) return;

  const matchingAnimals = rows.value.filter((row) => {
    const animalId = row._id?.$oid || row._id;
    return selectedAnimalIds.value.has(animalId);
  });

  selectedRows.value = matchingAnimals;
}

function syncSelectedIdsFromRows() {
  const currentPageIds = new Set<string>();
  rows.value.forEach((row) => {
    const id = row._id?.$oid || row._id;
    if (id) currentPageIds.add(id);
  });

  const selectedOnPageIds = new Set<string>();
  selectedRows.value.forEach((animal) => {
    const id = animal._id?.$oid || animal._id;
    if (id) selectedOnPageIds.add(id);
  });

  const newIdSet = new Set(selectedAnimalIds.value);
  const newAnimalsMap = new Map(selectedAnimalsById.value);

  currentPageIds.forEach((id) => {
    if (!selectedOnPageIds.has(id)) {
      newIdSet.delete(id);
      newAnimalsMap.delete(id);
    }
  });

  selectedRows.value.forEach((animal) => {
    const id = animal._id?.$oid || animal._id;
    if (!id) return;

    newIdSet.add(id);
    newAnimalsMap.set(id, animal);
  });

  selectedAnimalIds.value = newIdSet;
  selectedAnimalsById.value = newAnimalsMap;
}

function reset() {
  columns.value = [];
  page.value = 1;
  rows.value = [];
  rowsPerPage.value = ACTIVE_ROWS_PER_PAGE;
  searchQuery.value = '';
  selectedRows.value = [];
  selectedAnimalIds.value = new Set();
  selectedAnimalsById.value = new Map();
  totalRecords.value = 0;
}

const selectedAdditionalAnimals = computed(() => Array.from(selectedAnimalsById.value.values()));

export default function () {
  return {
    ignoreStorageForRowsPerPage: true,
    activeColumns,
    columns,
    frozenFields,
    initialColumns,
    mainRequestUrlParams,
    page,
    rows,
    rowsPerPage,
    searchQuery,
    selectedRows,
    selectedAnimalIds,
    selectedAdditionalAnimals,
    size,
    totalRecords,

    reset,
    syncSelectedRowsFromIds,
    syncSelectedIdsFromRows,
  };
}
